import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query'
import { apiPaths } from '@/api/api-paths'
import { queryKeys } from '@/api/query-keys'
import { getApiAuthToken } from '@/components/auth/api-auth-token'

export type UserRead = {
  id: string
  keycloak_sub: string
}

type ApiResponse<T> = {
  message: string
  data: T
}

async function fetchWithAuthToken(url: string, options: RequestInit = {}): Promise<Response> {
  const token = getApiAuthToken()
  if (!token) throw new Error('No access token available')
  return fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  })
}

async function postSession(): Promise<UserRead> {
  const res = await fetchWithAuthToken(apiPaths.auth.session(), { method: 'POST' })

  if (!res.ok) {
    const text = (await res.text()) || res.statusText
    throw new Error(`Failed to create session: ${res.status} ${text}`)
  }

  const json: ApiResponse<UserRead> = await res.json()
  return json.data
}

export function useAuthSession(
  options?: Omit<UseMutationOptions<UserRead, Error, void>, 'mutationFn'>,
) {
  const queryClient = useQueryClient()
  const userOnSuccess = options?.onSuccess

  return useMutation({
    ...options,
    mutationFn: postSession,
    onSuccess: (data, variables, context, mutation) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() })
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.userMe() })
      userOnSuccess?.(data, variables, context, mutation)
    },
  })
}
